// 하이트필드 순수 상수 — zero-import(THREE·window 의존 없음).
// Web Worker(hfBuild.js/hfWorker.js)가 이 값을 물어도 워커 환경이 안전하도록 별도 파일로 분리했다.
// heightfieldData.js 가 이걸 import 후 re-export 하므로 여기가 단일 진실(SSOT).
// 렌더 메시(HeightfieldPoC)와 발밑 조회(hfSurfaceYAt)가 같은 값을 써야 발이 지면에 정확히 선다.

export const HF_RENDER_OFFSET = 0.06; // 렌더 메시 y 오프셋 — 발이 보이는 면 위에 서게
export const VOXEL_GRID_OFF = 0.5;    // 복셀 계단 격자 반 칸 이동 — 계단 모서리를 건축 1m 블록 경계(x.5)에 정합

// 굴착(Excavation) "셀 승격" — 편집이 일어난 8m 지형 셀만 1m 복셀 8×8 로 쪼갠다.
// 8 인 이유: 지형 셀 8m ÷ 건축 블록 1m(BLOCK_SIZE) = 정확히 8 → 나눗셈 오차 0.
// 렌더(hfBuild)·발밑(heightfieldData)·편집저장소(terrainEdits) 셋이 같은 값을 봐야 한다.
export const PROMOTE_SUB = 8;
// 컬럼당 굴착 비트 수 — 비트 k = "원지표에서 k 번째 아래 1m 칸이 비었다".
// 이 비트마스크가 있어야 "흙 밑에 빈 칸, 그 밑에 또 흙"이 표현되고 그게 곧 천장(오버행)이다.
// 숫자 하나("몇 칸 깎았다")로는 빈 공간이 늘 맨 위에 붙는다.
//
// [왜 32 → 128 인가 — 2026-08-02]
//   32 일 때 **지표 아래 32m 를 넘는 실내는 만들 수 없었다.** 정확히는 만들 수는 있는데
//   (블록으로 방을 지으면 된다) **걸어 들어갈 수가 없었다**: 굴착된 컬럼 옆에 안 파인 컬럼이
//   있으면 hfBuild 가 그 경계에 벽면을 그린다(solidAt). 그래서 통로가 32m 한도에 닿는 순간
//   그 자리에 폭 15m·높이 26m 짜리 암벽이 서고, 충돌은 없어서 통과는 되는데 눈에는 막혀 있었다
//   (남산 봉인 동굴 실플레이 제보). 「블록으로 방을 짓고 굴착으로 입구만 뚫는다」는 접근이
//   원리적으로 이 이음매를 못 없앤다 — 마인크래프트가 동굴을 **볼륨에서 깎아내는** 이유다.
//   → 매질을 늘렸다. 128 = 지하 128m. 남산 홀 바닥이 지표 아래 최대 113m 라 여기서 나온 수다.
// [비용] 컬럼당 4바이트 → 16바이트. 셀(64컬럼) 256B → 1024B. 승격된 셀만 저장하므로
//   실제 증가분은 「판 적 있는 셀 수 × 768B」다.
export const CAVE_BITS = 128;
// 컬럼 하나가 차지하는 uint32 낱말 수. 마스크 배열은 Uint32Array(64 * CAVE_WORDS) 이고
// 컬럼 k 는 [k*CAVE_WORDS, k*CAVE_WORDS+CAVE_WORDS) 를 쓴다 — 서버 bytea 와 바이트 단위로 같다.
export const CAVE_WORDS = CAVE_BITS >>> 5;
// 플레이어가 팔 수 있는 깊이 한도 — **형식 용량(CAVE_BITS)과 다른 축이다.**
// 용량을 늘렸다고 유저 곡괭이가 128m 를 뚫으면 「수직갱 특례」(estate/service.py MINE_SHAFT_*)가
// 의도한 지형 훼손 한도가 4배로 풀린다. 그래서 게임 규칙은 여기 따로 못박고, 형식은 형식대로 둔다.
export const PLAYER_CUT_BITS = 32;
// 굴 안에서 걸어 올라갈 수 있는 한 계단 높이(m). **playerSurface.STEP_UP 과 같은 값이어야 한다** —
// 발밑 조회(hfSurfaceYAt)가 이 높이까지 위를 먼저 보고, 이동 판정(terrainWall)이 이 높이를
// 넘으면 벽이라고 한다. 두 값이 갈라지면 "올라설 수는 있는데 못 들어가는 칸" 또는 그 반대가 난다.
// (여기 두는 이유: heightfieldData 가 player 코드를 import 하면 순환이 된다. 상수는 zero-import 쪽이 SSOT)
export const CAVE_STEP_UP = 1.1;

// ── 컬럼 마스크 접근자 (m = Uint32Array(64*CAVE_WORDS), k = 컬럼 인덱스) ────────
// 128비트는 JS number 로 안 들어간다. 값 하나로 다루던 걸 전부 이 함수들로 바꿨다 —
// 렌더(hfBuild)·발밑(heightfieldData)·저장소(terrainEdits)가 같은 접근자를 쓰게 해서
// "한쪽만 32비트로 읽는" 사고를 원천 차단한다(SOP §5-C 와 같은 이유).
export const colBit = (m, k, d) => (m[k * CAVE_WORDS + (d >>> 5)] >>> (d & 31)) & 1;
export const colSetBit = (m, k, d) => {
  const i = k * CAVE_WORDS + (d >>> 5);
  m[i] = (m[i] | (1 << (d & 31))) >>> 0;
};
export const colClrBit = (m, k, d) => {
  const i = k * CAVE_WORDS + (d >>> 5);
  m[i] = (m[i] & ~(1 << (d & 31))) >>> 0;
};
// 선두 연속 빈칸 길이 = 위에서 깎여나간 깊이. 지표면은 여기까지 내려와 있다.
export const colLead = (m, k) => {
  let d = 0;
  while (d < CAVE_BITS && colBit(m, k, d)) d += 1;
  return d;
};
// 「이 컬럼에서 곡괭이가 지울 흙 칸」 — from 깊이부터 아래로 훑어 처음 만나는 흙(비트 0).
// **colLead 는 이 함수의 from=0 인 경우다**(지표에서 시작 = 하늘이 열린 노천).
//
// [왜 필요했나 — 2026-08-02 실사고] 굴착이 colLead 만 봤다. 그래서 게임이 파 준 챔버
//   (원지표 아래 20~30m, 머리 위는 암반이라 lead=0) 안에서 곡괭이를 휘두르면 발밑이 아니라
//   **머리 위 지붕을 위에서부터** 벗겼다. 발밑은 한 칸도 안 내려가고, 6번 휘두르면
//   "넓게 벗겨내는 건 6m 까지" 로 거절당한다 — 즉 **게임이 파 준 갱 안에서 채굴이 불가능했다.**
//   from 을 발밑 밴드로 주면 같은 식이 "발 아래 첫 흙"을 가리킨다. 규칙이 하나로 남는다.
export const colDigFace = (m, k, from = 0) => {
  let d = Math.max(0, from | 0);
  while (d < CAVE_BITS && colBit(m, k, d)) d += 1;
  return d;
};
// X-ray 용 — 이 컬럼에서 **가장 깊은 빈 칸 바로 아래**까지의 깊이.
//
// `colLead` 는 「지표에서 연속으로 깎인 만큼」이라, 흙에 덮인 굴(lead=0)은 0 을 준다.
// 그래서 지표가 안 내려가고 굴이 안 보인다 — X-ray 가 하려는 건 정확히 그 반대다.
// 여기서는 **맨 아래 빈 칸까지 통째로 걷어** 렌더 지표를 굴 바닥에 앉힌다.
// 즉 「지붕을 벗겨 도랑으로 만든다」 — 흙 밑 굴이 위에서 훤히 보인다.
// 판 적 없는 컬럼(비트 0)은 0 을 주므로 **원지형은 한 톨도 안 변한다.**
export const colXrayLead = (m, k) => {
  const b = k * CAVE_WORDS;
  for (let w = CAVE_WORDS - 1; w >= 0; w -= 1) {
    const v = m[b + w] >>> 0;
    if (v === 0) continue;
    let top = 31;
    while (top >= 0 && !((v >>> top) & 1)) top -= 1;
    return (w << 5) + top + 1;      // 그 칸까지 비었으니 지표는 한 칸 더 내려간다
  }
  return 0;
};
export const colAny = (m, k) => {
  const b = k * CAVE_WORDS;
  for (let w = 0; w < CAVE_WORDS; w += 1) if (m[b + w] !== 0) return true;
  return false;
};
// 선두 런 **아래**에 아직 빈 칸이 있나 = 흙 밑 빈 공간(=동굴·오버행)을 가진 컬럼인가.
// 낱말 단위로 판정한다 — 컬럼마다 128번 도는 걸 셀당 64컬럼 × 타일마다 하면 실측이 무너진다.
/**
 * 선두 런(지표) 아래 **가장 얕은 빈 비트**의 깊이 — 즉 그 컬럼 굴의 **천장**. 없으면 -1.
 *
 * 왜 필요한가 (2026-08-16) — 지상에 서 있을 때 발밑 40m 터널을 그릴 이유가 없는데,
 * 「그릴지 말지」를 정하려면 **굴 천장이 카메라보다 얼마나 아래인가**를 알아야 한다.
 * 지표 높이(`bands`)로는 못 가른다 — 산 위에 서 있어도, 갱구 바닥에 서 있어도
 * 「카메라 ≈ 지표」라 값이 같다. 갈리는 건 천장 깊이뿐이다.
 */
export const colCaveTopBit = (m, k, lead) => {
  const b = k * CAVE_WORDS;
  for (let w = 0; w < CAVE_WORDS; w += 1) {
    const lo = w << 5;
    if (lead >= lo + 32) continue;
    let v = m[b + w] >>> 0;
    if (lead > lo) { const s = lead - lo; v = (v >>> s) << s; }   // 선두 런 구간을 0 으로
    if (v !== 0) return lo + (31 - Math.clz32(v & -v));           // 최하위 set 비트
  }
  return -1;
};
export const colCaveBelow = (m, k, lead) => {
  const b = k * CAVE_WORDS;
  for (let w = 0; w < CAVE_WORDS; w += 1) {
    const lo = w << 5;
    if (lead >= lo + 32) continue;
    let v = m[b + w] >>> 0;
    if (lead > lo) { const s = lead - lo; v = (v >>> s) << s; }   // 선두 런 구간을 0 으로
    if (v !== 0) return true;
  }
  return false;
};
// 전역 셀 인덱스 — 타일 윈도·LOD 와 무관한 셀의 절대 식별자.
// 워커/발밑/렌더가 "같은 셀"을 가리키려면 윈도 원점이 아니라 월드 원점 기준이어야 한다.
export const globalCellIdx = (originW, cell, i) => Math.round(originW / cell) + i;

// ── 경사 적응 밴드 ─────────────────────────────────────────────────
// 왜 여기 있나: 지형 높이를 밴드로 스냅하는 지점이 **두 군데**다 —
//   렌더 hfBuild.buildSteppedArrays  /  발밑·충돌 heightfieldData.hfSurfaceYAt
// 한쪽만 고치면 캐릭터가 지형에 묻히거나 뜬다. 이 프로젝트가 **세 번** 겪은 사고라
// (커튼월·밴드 양자화·서브셀, SOP §5-C) 두 곳이 반드시 이 함수 하나를 부르게 못박는다.
//
// [문제] 테라스 폭 = 밴드 ÷ 경사. 밴드가 같아도 경사가 완만하면 한 단이 수십 m 가 되어
//   평지가 논두렁(계단논)이 된다. 후암동 실측 24.2% / 서울 전역 31.0% 가 그 구간.
//
// [왜 밴드를 줄이면 안 되나] 완경사에서 테라스 16m 를 얻으려면 밴드 0.48m 가 필요한데,
//   §11.2 가 밴드를 **1m 의 정수배**로 못박는다(건축 블록 바닥이 Math.floor 정렬 —
//   0.5m 밴드는 블록이 반 칸 뜬다). 그래서 밴드는 **위로만** 갈 수 있다.
//
// [해법 두 축]
//   ① 완경사 평탄화 — 경사가 낮을수록 양자화를 풀어 원래 높이로 되돌린다.
//      단이 사라지는 게 아니라 단 높이가 경사에 비례해 작아진다(완전 평지 → 단 0).
//   ② 급경사 밴드 확대 — 급한 곳은 밴드를 정수배(×2·×3)로 키워 절벽 한 면을 크고 극적으로.
//      정수배라 블록 정합이 유지된다. 이게 ①에서 못 쓴 "경사 적응 밴드"를 반대 방향으로 쓴 것.
const sstep = (e0, e1, x) => {
  if (e1 <= e0) return x >= e1 ? 1 : 0;
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};
// 급경사 판정 구간(tanθ). subSlope 기본 0.25(서브셀)·셰이더 uCliffLo 0.5(암반) 사이에 맞췄다.
//
// ⚠ **여기서부터 밴드가 커지는 게 아니다.** 아래 terrainBandY 가 배수를 `Math.round` 로 정수
//   스냅하므로(블록 정합상 정수여야 한다), ×2 가 실제로 걸리려면 sstep ≥ 0.5 — 즉 **경사
//   0.625(32°) 이상**이어야 한다. LO 는 "0 에서 벗어나기 시작하는 지점"일 뿐 발동점이 아니다.
//   ×3 은 0.529(28°)에서 ×2, 0.721(36°)에서 ×3.
//
// [실측 2026-08-01 — front/scripts/slope_adapt_audit.mjs] 그 위에 있는 땅이 거의 없다:
//   전 서울 1.2% · 달빛 망루지 산비탈 4.0% · **후암동 근경 0.0%**.
//   그래서 절벽밴드 ×2·×3 을 켜도 이웃 셀 낙차 분포가 사실상 안 변한다(산비탈 ≥2m 벽
//   36.2% → 36.2%). 32° 이상 사면은 512m 블록 81곳에 몰려 있는데 전부 「내집」에서 9~19km,
//   즉 관악·북한산 급 외곽이다. **현 콘텐츠 권역에서 이 슬라이더는 사실상 무동작**이고,
//   산악지로 콘텐츠가 확장될 때 의미가 생긴다. STEEP_LO 를 0.12 까지 낮춰도 전 서울 2.8%·
//   후암 근경 0.0% 라, LO 를 만지는 것으로는 안 살아난다(문제는 임계가 아니라 지형이다).
export const STEEP_LO = 0.35, STEEP_HI = 0.9;

// ── 「완경사 평탄화」는 **구웠다** — 값을 0 으로 고정한다 ────────────
//
// [무엇을 굽나] `hfFlattenSlope` 슬라이더의 값. 렌더(hfBuild)와 발밑(heightfieldData)이
//   **둘 다 이 상수를 본다** — 한쪽만 고치면 캐릭터가 묻히거나 뜬다(SOP §5-C, 세 번 겪음).
//
// [왜 0 인가 — 성능이다. 그림은 값을 치른다]
//   평탄화는 경사 이하 셀의 «양자화를 푼다». 즉 이웃 상판이 같은 높이인 경우를 없앤다.
//   그 결과 두 가지가 동시에 벌어지고 있었다(실측 2026-08-23, 미성동 격자권 3×3 타일):
//     ① **상판을 병합할 수 없다** — 등가 Y 직사각 병합 시뮬이 1.06배(=이득 0).
//        평탄화 0 이면 같은 시뮬이 **6.43배**(근경 상판 23,776 → 3,698 tri).
//     ② **벽(riser)이 두 배가 된다** — 몇 cm 짜리 낙차마다 벽이 선다.
//        벽 쿼드 84,689 → 43,408. 낙차 15cm 미만이 29.8%(삼각형 50,554장)였다.
//   총합 263,184 → 180,622 tri (−31%), 여기에 상판 그리디가 더 얹힌다.
//
// [무엇을 잃나 — 숨기지 않는다] 완경사가 계단(논두렁)으로 보인다. 2026-08-01 에 이 그림을
//   보고 기본값을 0 → 0.30 으로 올렸었다. 그 결정을 **성능을 사려고 되돌린 것**이고,
//   되돌린 사람은 명관님이다.
//   그때 0.30 을 고른 근거(육안 검수)는 지금도 유효하다 — 바뀐 건 근거가 아니라 **우선순위**다.
//
// [되돌리기] 이 상수를 0.30 으로 되돌리면 그림은 8/1~8/22 와 같아진다. 단 **상판 그리디가
//   자동으로 무동작이 되므로**(위 ① 참조) 삼각형은 그때 값으로 통째로 돌아간다. 슬라이더는
//   UI 에 남아 있지만 「구웠음」으로 잠겨 있다(TerrainDetailPanel).
export const BAKED_FLATTEN = 0;

// 지형 «모양» 지문(`terrainBakeSig.liveShape`)에 실리는 축들 — 유저 설정이 아니라 상수다.
// 하나라도 구운 값과 다르면 `lodBake.verify()` 가 자산 827장을 통째로 폐기하고 런타임
// 생성으로 떨어진다(실측 5.2초 → 18.9초). 그림은 같아서 눈으로는 못 가른다.
// ⚠ 짝은 `front/scripts/_terrain_bake_env.mjs` 의 `FROZEN` — 이 표를 읽어야 한다. 두 벌이면 갈린다.
// ⚠ 값을 바꾸면 `node front/scripts/bake_terrain_all.mjs` 로 다시 굽는다.
// 손잡이로 되돌리려면 여기서 키를 빼고 `RpgWorld` 가 설정을 읽게 한다(UI 는 따라온다).
export const BAKED_SHAPE = {
  band: 1,          // 양자화 밴드 높이(m). 건축 블록 1m 와 맞물려 1m 정수배가 강제다
  steepMul: 1,      // 급경사 밴드배수. 현 콘텐츠 권역에선 사실상 무동작(STEEP_LO 주석)
  sub: 2,           // 구운 LOD 자산 827장과 일치하는 경사 적응 서브셀
  subSlope: 0.25,   // sub 가 1 이면 지문에서 0 으로 눕는다 — 지금은 무동작
};

// ── 구운 셰이더 값 둘 ──────────────────────────────────────────
// 판정은 하나였다 — **화면에서 구분이 안 되는데 픽셀 비용은 낸다.**
//
// [입체감 BAKED_RELIEF] albedo 의 화면 미분(dFdx/dFdy)으로 만든 «가짜 노멀». 이미 실운영
//   프리셋이 0 이었는데(originSettings.hfRelief=0), **0 이어도 계산은 매 픽셀 그대로 돌고
//   있었다** — 유니폼만 0 이라 cav=1·dirL=1 로 «결과»만 무효가 됐을 뿐이다.
//   게다가 이 항은 2026-07-29 에 근거리 램프 버그(카메라 주변이 통째로 절반 밝기)가 있어
//   8~34m 램프로 눌러 둔 상태였다 — 「켜도 근경엔 거의 안 걸리는 값」이다.
//
// [상판얼룩 BAKED_TOP_MOTTLE] 8m 주기 명도 얼룩. 정점색에서 프래그먼트로 옮긴 직후
//   0.00 ↔ 0.16 두 컷을 눈으로 비교했는데 **구분이 안 됐다**.
//   진폭이 최대 ±8%p 인데 지형이 채도 높은 초록 + 텍스처라 그 안에 묻힌다.
//   대신 vnoise = hash21 4회 = sin 4회를 «지형을 덮는 모든 픽셀»에서 낸다.
//
// ⚠ 둘 다 **셰이더에서 유니폼 분기로 감쌌다** — 0 이면 계산 자체를 건너뛴다.
//   상수를 0 으로 두는 것만으로는 안 줄어든다(그게 입체감이 여태 새던 이유다).
//   되살리려면 이 상수를 올린다. 슬라이더는 🔒 로 잠가 뒀다(TerrainDetailPanel).
export const BAKED_RELIEF = 0;
export const BAKED_TOP_MOTTLE = 0;

// [반복깨기 BAKED_DETILE] 위 둘과 «성격이 다르다» — 이건 안 보이는 걸 끈 게 아니라
//   **보이는 것을 판 것**이다. 결정 근거를 그대로 남긴다.
//
//   무엇이었나: 타일 셀마다 해시로 90° 회전을 줘 8m 텍스처 격자 반복을 깨는 기법.
//   비용: `noTileAmt` 가 0.01 을 넘는 순간 **텍스처를 두 번 뽑는다**(+ hash21 sin 1 · cos/sin 2).
//     호출부가 6곳(잔디 A/B/C · 도로 · 암반 가로/세로)이라 **지형 텍스처 페치 6 → 12**.
//     ⚠ 0.30 이든 1.00 이든 «똑같이» 2배다 — 임계를 넘느냐 마느냐만 있다.
//   왜 껐나: 이 셰이더에서 가장 큰 픽셀 노브이고, 텍스처 페치는 폰의 첫 병목이다.
//     입체감·상판얼룩(ALU 몇 개)과는 자릿수가 다르다. 사용자 판단으로 페치 절반을 택했다.
//
//   ⚠ **반복 무늬가 보이면 이 값을 되살린다** — 8m 격자가 드러나는 걸 막던 값이다.
//     되살리는 비용은 「페치 2배」 하나뿐이고 다른 부작용은 없다. 0.05 만 넘겨도 전액이니
//     되살릴 거면 **효과를 최대로**(1.0) 쓰는 게 값어치가 좋다 — 중간값은 비용이 같다.
//   [남은 대체 수단] 반복을 깨는 축이 이것만은 아니다 — 바이옴 매크로 색변화(cFbm2 160m·48m)와
//     컨트롤맵 틴트가 «더 큰 주기»에서 이미 깨고 있다. 이 값이 담당하던 건 8m 주기뿐이다.
export const BAKED_DETILE = 0;
// ★ 2026-08-25 — **PC 만 되살린다.** 위 주석이 「반복 무늬가 보이면 되살려라」고 적어 둔
//   조건이 실제로 걸렸다: 후암 남동 고지(7940,−4742) 눈검수에서 8m 격자가 드러났다.
//
//   [왜 폰은 그대로 0 인가] 끈 이유가 처음부터 **폰**이었다 — 「텍스처 페치는 폰의 첫
//   병목」. PC 실측은 그 자리에서 FPS 62 · 프레임 12.2ms · 드로콜 213 이라 페치 2배를
//   낼 여유가 있고, 폰은 그렇지 않다. **값은 갈라도 되고 규칙은 하나여야 한다**
//   (`device_split_rules.md`) — 규칙은 「반복이 보이면 켠다」 하나이고 임계만 기기별이다.
//
//   [왜 1.0 인가] 0.05 만 넘겨도 비용이 전액이다(위 주석). 중간값은 **같은 값을 내고 덜
//   받는** 선택이므로 되살릴 거면 최대로 쓴다.
//
//   ⚠ 이 파일은 zero-import 다(워커가 문다). 기기 판정은 여기서 **안 한다** — 값만 둘로
//     내놓고 고르는 건 소비처(`HeightfieldPoC.jsx`)가 한다.
// ★ 2026-08-25 저녁 — **되돌렸다(1.0 → 0).** 켠 지 몇 분 만이다.
//   지적: 「지형 반복깨기 병목 심하다고 하지 않았냐」 — 맞다. 내가 바로 위 주석에
//   **「이 셰이더에서 가장 큰 픽셀 노브」**라고 적어 놓고 켰다.
//   근거로 든 「PC 는 FPS 62 라 여유가 있다」는 **한 자리에서 잰 값**이고, 그 자리는
//   후암 남동 고지(드로콜 213 · tri 131k)로 이 월드에서 가벼운 축이다.
//   여의도·명동을 다시 세우거나 시공물이 많은 동에 서면 그 여유가 없다.
//   ⇒ **다시 켤 거면 무거운 자리에서 A/B 를 재고 나서다.** 8m 격자 반복은 페치를 안 쓰는
//     축(컨트롤맵 틴트·바이옴 매크로 색변화 주기 조정)으로 먼저 시도한다.
export const BAKED_DETILE_PC = 0;


// rawY = baseY + h + HF_RENDER_OFFSET (양자화 전 실제 높이)
// flatten = 「완경사 평탄화」 임계(tanθ). 0 이면 기능 OFF — 결과가 종전과 **완전히 동일**.
//           이 경사 이하는 완전히 매끈, 2배 지점부터 완전히 밴드(전이폭 자동 → 슬라이더 1개).
// steepMul = 「급경사 밴드배수」 1~3. 1 이면 OFF.
export function terrainBandY(rawY, slope, band, flatten = 0, steepMul = 1) {
  if (!(band > 0)) return rawY;
  let b = band;
  if (steepMul > 1) {
    // 배수는 **정수**로 스냅한다 — 1.5배 같은 값은 상판을 정수에서 떨어뜨려 블록이 어긋난다.
    b = band * (1 + Math.round((steepMul - 1) * sstep(STEEP_LO, STEEP_HI, slope)));
  }
  const q = Math.round(rawY / b) * b;
  if (flatten > 0) {
    const t = sstep(flatten, flatten * 2, slope);
    return q * t + rawY * (1 - t);
  }
  return q;
}

// ── 지층 단구 — 경사지 표고를 층으로 다시 접어 «절벽 + 풀 계단»을 만든다 ──────
// 높이를 band(m) 띠로 자르고 띠마다 1~3층으로 무작위 분할(층 두께 약 1~24m) — 층 높이의 cliffRise 를
// 층 가로폭의 (1 - cliff) 에 몰아 절벽을 세운다. 층 경계는 warpM 만큼 지형 따라 휜다. 트인 곳(31m 노이즈 breach) · 아주 가파른 곳(steep) 은 원지형 그대로 둬 오르는 길로 남긴다.
// 경사 slopeLo 이하 무변경, slopeHi 이상 전량, steepLo~steepHi 에서 다시 잦아든다. on=0 이면 무동작.
// vary=1 이면 자리마다 다르게 — regionM 크기 지역(경계는 휜다)마다 띠 높이(bands 중 하나)·층 수(1~layersMax)·
// 층 위상·트인 곳 주기(breachM 중 하나)를 뽑고, 선반 깊이(cliff)·벽 몫(cliffRise)은 노이즈로 연속으로 바꾼다.
// 지역 경계에서는 층이 어긋나 단층처럼 끊긴다. vary=0 이면 전역 상수 한 벌(band·cliff·cliffRise)이다.
// ⚠ 값을 바꾸면 LOD 를 다시 굽는다(`terrainBakeSig.liveShape` 의 strata 키가 바뀐다).
export const BAKED_STRATA = {
  on: 1, band: 24, warpM: 14, cliff: 0.9, cliffRise: 0.78, slopeLo: 0.18, slopeHi: 0.35,
  steepLo: 0.55, steepHi: 0.75, breachLo: 0.50, breachHi: 0.62,
  vary: 1, regionM: 480, bands: [14, 20, 26, 32, 40], layersMax: 4,
  cliffLo: 0.8, cliffHi: 0.95, riseLo: 0.62, riseHi: 0.9, breachM: [20, 31, 48, 75],
};

// 정수 해시 밸류 노이즈 — sin 해시와 달리 Node·브라우저와 무관하게 같은 값을 낸다.
const sHash = (ix, iz) => {
  let h = Math.imul(ix | 0, 374761393) ^ Math.imul(iz | 0, 668265263);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
};
const sNoise = (x, z) => {
  const ix = Math.floor(x), iz = Math.floor(z);
  let fx = x - ix, fz = z - iz;
  fx = fx * fx * (3 - 2 * fx); fz = fz * fz * (3 - 2 * fz);
  const a = sHash(ix, iz), b = sHash(ix + 1, iz), c = sHash(ix, iz + 1), d = sHash(ix + 1, iz + 1);
  return (a + (b - a) * fx) * (1 - fz) + (c + (d - c) * fx) * fz;
};

/** 지층 단구를 적용한 «양자화 전» 높이.
 *  @param {number} y 생높이(월드 Y) @param {number} x @param {number} z 노이즈 표본점(칸·서브칸 중심)
 *  @param {number} slope tanθ @param {number} amt 0~1 제외 마스크 세기 */
export function strataTerraceY(y, x, z, slope, amt, p = BAKED_STRATA) {
  if (!p || !p.on || !(amt > 0)) return y;
  if (p.vary) return strataVaryY(y, x, z, slope, amt, p);
  // 트인 곳·아주 가파른 곳은 원지형 그대로 — 원지형은 점프(2m)로 오를 수 있는 단이라 길이 된다.
  const open = sstep(p.breachLo, p.breachHi, sNoise(x / 31 + 3, z / 31 + 9));
  const t = amt * sstep(p.slopeLo, p.slopeHi, slope) * (1 - sstep(p.steepLo, p.steepHi, slope)) * (1 - open);
  if (!(t > 0)) return y;
  const B = p.band;
  const warp = (sNoise(x / 53 + 7, z / 53) - 0.5) * p.warpM + (sNoise(x / 17 + 1, z / 17 + 5) - 0.5) * 3;
  const yw = y + warp;
  const kb = Math.floor(yw / B), fb = yw - kb * B;
  // 띠 하나를 1~3층으로 — 경계는 띠 번호의 해시라 위아래 단조(높은 땅이 낮아지지 않는다).
  const n = 1 + Math.floor(sHash(kb, 11) * 3);
  const a = sHash(kb, 23), c = sHash(kb, 37);
  let lo = 0, hi = B;
  if (n === 2) {
    const s1 = B * (0.3 + 0.4 * a);
    if (fb < s1) hi = s1; else lo = s1;
  } else if (n === 3) {
    const s1 = B * (0.18 + 0.3 * Math.min(a, c)), s2 = B * (0.52 + 0.3 * Math.max(a, c));
    if (fb < s1) hi = s1; else if (fb < s2) { lo = s1; hi = s2; } else lo = s2;
  }
  const T = hi - lo, f = (fb - lo) / T;
  const r = p.cliff, g = 1 - p.cliffRise;
  const fr = f < r ? (f / r) * g : g + ((f - r) / (1 - r)) * p.cliffRise;
  return y + (kb * B + lo + fr * T - warp - y) * t;
}

/** 지역 씨앗 0~65535 — regionM 격자를 도메인 워프로 휘어 경계가 곧은 선으로 안 읽히게 한다. */
function strataRegionSeed(x, z, p) {
  const R = p.regionM, w = R * 0.35;
  const rx = Math.floor((x + (sNoise(x / 190 + 2, z / 190 + 8) - 0.5) * w) / R);
  const rz = Math.floor((z + (sNoise(x / 190 + 19, z / 190 + 4) - 0.5) * w) / R);
  return Math.floor(sHash(rx + 7, rz + 3) * 65536);
}

/** strataTerraceY 의 vary=1 판 — 층고·선반 깊이·벽 길이가 자리마다 다르다. 한 자리 안에서는 높이에 대해 단조. */
function strataVaryY(y, x, z, slope, amt, p) {
  const seed = strataRegionSeed(x, z, p);
  const pick = (arr, k) => arr[Math.min(arr.length - 1, Math.floor(sHash(seed, k) * arr.length))];
  const bm = pick(p.breachM, 5);
  const open = sstep(p.breachLo, p.breachHi, sNoise(x / bm + 3, z / bm + 9));
  const t = amt * sstep(p.slopeLo, p.slopeHi, slope) * (1 - sstep(p.steepLo, p.steepHi, slope)) * (1 - open);
  if (!(t > 0)) return y;
  const B = pick(p.bands, 1);
  const warp = (sNoise(x / 53 + 7, z / 53) - 0.5) * p.warpM + (sNoise(x / 17 + 1, z / 17 + 5) - 0.5) * 3;
  const yw = y + warp + sHash(seed, 2) * B;       // 지역마다 층 위상을 옮긴다 — 경계에서 단층이 된다
  const kb = Math.floor(yw / B), fb = yw - kb * B;
  const kh = kb + seed * 97;
  const n = 1 + Math.floor(sHash(kh, 11) * p.layersMax);
  // 층 경계 k/n 에 ±0.35/n 흔들림 — 순서가 안 뒤집히고 층 두께는 최소 0.3B/n.
  let lo = 0, hi = B;
  for (let k = 1; k < n; k++) {
    const c = B * (k + (sHash(kh, 23 + k * 14) - 0.5) * 0.7) / n;
    if (fb < c) { hi = c; break; }
    lo = c;
  }
  const T = hi - lo, f = (fb - lo) / T;
  const r = p.cliffLo + (p.cliffHi - p.cliffLo) * sNoise(x / 350 + 5, z / 350 + 71);
  const rise = p.riseLo + (p.riseHi - p.riseLo) * sNoise(x / 300 + 33, z / 300 + 2);
  const g = 1 - rise;
  const fr = f < r ? (f / r) * g : g + ((f - r) / (1 - r)) * rise;
  return y + (kb * B + lo + fr * T - warp - sHash(seed, 2) * B - y) * t;
}

// ── 도로 답판 분할 s — 경사 적응 ───────────────────────────
// [무엇] 도로 셀의 상판 분할 s 를 「경사에 필요한 만큼만」 올린다. 종전엔 도로 셀이면
//   무조건 s = roadSub(=4) 였다 — 평지 도로도 16 서브블록으로 쪼갰다.
//
// [왜] roadSub 이 존재하는 이유는 단 하나, «셀 폭 × 경사» 짜리 잔단을 줄이는 것이다
//   (roadCells.js 상단). 잔단 ∝ 1/s 이므로 **평지(경사≈0)에서는 s 를 올려도 얻는 게 없다.**
//   격자형 밀집 도심은 도로 셀이 근경의 56% 인데(실측, 2026-08-22) 그 대부분이 평지다.
//
// [실측 — 격자 밀집구역 근경 260m, 도로셀 56%]
//     s=1        30,582 tri /  86ms
//     s=2        40,708 tri /  86ms   (1.33x)
//     s=4(종전)  79,670 tri / 128ms   (2.61x)
//   즉 도로서브셀 하나가 근경 삼각형을 **2.6배**로 만들고 있었다. (사용자 가설 「16배」는
//   기각 — 밴드가 같은 서브블록은 greedy 병합으로 합쳐지므로 s² 만큼 늘지 않는다.)
//
// [규칙] 목표 잔단 targetM 을 정하고 s = ceil(경사 × 셀폭 / targetM) 을 roadSub 로 캡한다.
//   기본 0.18m 는 **종전 최선값과 같다**(후암 경사 0.09 × 8m / 4 = 0.18) — 즉 s=4 가
//   필요했던 곳은 그대로 4 를 받고, 평지·완경사만 1~2 로 내려온다. **품질 무손실 절감.**
//
// ⚠ 렌더(hfBuild.getCell)와 발밑(heightfieldData.hfSurfaceYAt)이 **이 함수 하나**를 부른다.
//   한쪽만 고치면 캐릭터가 도로에 묻히거나 뜬다 — 이 프로젝트가 세 번 겪은 사고(SOP §5-C).
export const ROAD_STEP_TARGET_M = 0.18;
export function roadSubFor(slope, cellW, roadSub, targetM = ROAD_STEP_TARGET_M) {
  if (!(roadSub > 1)) return 1;
  if (!(targetM > 0)) return roadSub;             // 0 = 적응 끔(종전과 완전히 동일)
  const need = Math.ceil((Math.abs(slope) * cellW) / targetM);
  return Math.max(1, Math.min(roadSub, need));
}

// ── 「도로 특례」 폐기 ───────────────────────────────────────────
//
// [무엇을 껐나] 길에만 걸려 있던 두 기제 — **도로서브셀**(`hfRoadSub`: 8m 지형 셀을 도로
//   칸에서만 4×4 로 쪼개 각각 양자화)과 **도로정지**(`roadGrade`: 반경 12m 박스 평균 ±1.5m).
//
// [왜] 둘이 서로 싸우고 있었다 — 서브셀이 길에 잔 계단을 만들고, 정지가 그걸 다시 지웠다.
//   실측(후암동 간선, 1m 칸 30,430 · `road_block_stair_probe.mjs` 파이프라인 재현):
//     서브셀 4 · 정지 OFF   단 13.0% · 간격  7.7m · 가로지르는 단 15.4%
//     서브셀 1 · 정지 OFF   단  7.9% · 간격 12.6m · 가로지르는 단  8.7%   ← 지금
//     정지 ON(서브셀 무관)  단  7.0% · 간격 14.4m · 가로지르는 단  6.9%
//   즉 **정지가 서브셀 끄기보다 추가로 버는 건 0.9%p 뿐**인데, 그 대가가 셋이었다:
//     ① 32m 블록 557개 중 16개(3%)에서 «오히려 악화»(한도에 물린 칸의 경계가 새 단을 만든다)
//     ② 발밑(`roadGradeAt`, ensure 없음)과 렌더(`ensureRoadGradeAt`)의 **±1.5m 비대칭**
//     ③ 타일 굽기 비용 + Int16 상주
//   그리고 이 게임의 땅은 이미 `world_chunk` 1m 블록이다 — **길만 다른 눈금을 쓸 이유가 없다.**
//
// [되돌리는 문] `?roadspecial=1` 로 한 판만 되살린다. 값(슬라이더·토글)은 안 지웠다 —
//   패널에서 회색으로 남아 「무엇이 있었고 왜 껐나」를 계속 말한다(groundMode.RETIRED_*).
// [근거 문서] /tech → 실험실·지형 → 「도로정지 ON/OFF — 실지형 3D 비교」(lab-road-grade)
export const ROAD_SPECIAL = (() => {
  try { return new URLSearchParams(location.search).get('roadspecial') === '1'; }
  catch { return false; }   // 워커·SSR 등 location 이 없는 자리 — 폐기가 기본값이다
})();

// ── ★ LOD 칸의 «세로 눈금» — 판때기(1m)인가 정육면체(S미터)인가 ──
//
// [무엇을 고르는 값인가] LOD 칸은 XZ 가 S미터다. Y 를 어떻게 잴지가 두 갈래다:
//   · `false` = **판때기** — 밟는 면 S미터 · 단 높이 **1m**. 지형의 «모양»이 남는다.
//   · `true`  = **정육면체** — 상판을 S 의 배수로 내림해 단 높이도 S. 마크의 1:1 비율.
//
// [왜 판때기가 기본인가 — 2026-08-30 지시] 「평면은 4×4 유지하면서 높이만 1m 간격으로
//   되는 블록지형을 기본으로」. 정육면체는 그날 오전에 한 번 켰다가 되돌렸다 — 근거는
//   「4×4×1 판때기가 논두렁으로 읽힌다」였는데, **단 높이가 칸 크기와 같아지면 S=8 에서
//   단이 8m** 가 되어 캐릭터(2m) 키의 4배짜리 절벽이 온 산에 선다. 논두렁을 고치려다
//   걸어 오를 수 없는 벽을 만든 것이라, 대가가 병보다 컸다.
//
// ⚠ **이 값을 바꾸면 구운 자산이 통째로 낡는다** — `terrainBakeSig.GEN_FIELDS.lodTopQuant`
//   가 이 값을 싣는다. 바꿨으면 `node front/scripts/bake_terrain_lod.mjs --scale <S> ...`
//   로 다시 굽는다. 안 구워도 그림은 맞다(그때그때 만든다) — 잃는 건 링이 차는 «속도»뿐이다.
// ⚠ 그리는 값과 발밑이 **같은 함수**를 봐야 한다(`terrainLodStore.lodQuantY` ↔
//   `playerSurface.lodQuantizedY`). 여기 하나만 고치면 둘 다 따라오므로 어긋날 수 없다.
// ── ★ 그 사이를 볼 문 — `?lodyq=N` ─────────────────────────
// [왜 필요했나] 위 두 갈래는 **1m 아니면 S** 뿐이라 «그 사이»를 못 본다. 그런데 계측이
//   가른 것은 정확히 그 사이다(축척 4m·같은 월드 면적, `_probe_floor_cap.mjs`):
//       세로 1m = 쿼드 131,395   ·   2m = 99,317(−24.4%)   ·   4m = 69,218(−47.3%)
//   4m 는 단이 캐릭터(2m) 키의 두 배라 못 오른다 — 그래서 되돌린 것이 맞다. 그런데
//   **2m 는 아직 아무도 «본 적이 없다».** 값으로는 −24%를 사면서 단이 캐릭터 키와 같다.
// [그래서 문만 뚫는다] 기본값은 안 건드린다(`1` = 판때기). 눈으로 고르는 값이라,
//   판정 없이 기본을 바꾸면 그게 곧 「안 물어보고 문을 하나 더 낸 것」이다.
// ⚠ 이 값을 바꾸면 **구운 자산이 낡는다** — `terrainBakeSig.GEN_FIELDS.lodTopQuant` 가 싣는다.
//   URL 해치로 한 판만 볼 때는 폴백(그때그때 만들기)으로 떨어지므로 그림은 맞다.
// ── ★ LOD 칸 한 변(m) — **하나뿐이다** (2026-09-01) ─────────────────────────
//
// [왜 상수인가] 종전엔 기기 프로필이 축척을 갈랐다 — PC 4m · 폰 2m. 그래서
//   ① 굽는 자산이 «축척마다 한 벌»이었다(`*.lod.s2.json` + `*.lod.s4.json`).
//      s2 는 s4 의 네 배를 굽고 네 배를 받는데, 그 s2 를 보는 건 폰뿐이었다.
//   ② 「이 자리가 구워져 있나」의 답이 기기마다 달랐다 — 같은 좌표에서 PC 는 구운 걸
//      쓰고 폰은 런타임 생성으로 떨어진다. **디버깅이 안 되는 상태**가 이것이다.
//   ③ 축척이 바뀌면 LOD 영역 크기(`getRegion() × 축척`)가 같이 바뀌어 셀 마스크·
//      축출 격자·`terrainVoid` 칸 스냅이 전부 따라 흔들렸다.
//
// [고른 값 4m] 표고 원본이 8m DEM 이라 4m 는 그 절반 — 셀 하나를 2×2 로 본다.
//   2m 는 4×4 라 «없는 정보»를 네 배로 늘려 담을 뿐이고, 8m 는 칸 경계가 DEM 경계와
//   붙어 능선이 한 계단으로 뭉갠다.
//
// ⚠ 이 값을 바꾸면 **구운 자산이 통째로 낡는다**(경로에 축척이 들어 있다 —
//   `terrain_baked/lod/<key>/s<N>/`). 바꿨으면 `node front/scripts/bake_terrain_all.mjs`
//   로 다시 굽는다.
export const LOD_SCALE_M = 4;

// ── ★ 「안 구운 지형은 «그리지 않고 터진다»」 (2026-09-01) ─────────────────
//
// [왜 폴백을 없애나] 종전엔 안 구운 자리를 **런타임에 만들었다**. 그림은 같으니 아무도
//   모르고, 대신 그 자리에서 프레임이 멎는다 — 「가끔 렉 걸리는 게임」의 정체가 이것이다.
//   조용한 폴백은 성능 사고를 «영원히 안 잡히게» 만든다.
//
// [고른 규약] 구운 것만 그린다. 없으면 **즉시 던진다** — 그러면 굽는다.
//   · 매니페스트가 그 블록을 «모르면»  → throw (진짜 안 구운 자리)
//   · 매니페스트는 아는데 아직 «안 받았으면» → 아무것도 안 만들고 기다린다(정상)
// [탈출구] `?lodstrict=0` — 옛 폴백으로 되돌린다. 굽기 파이프라인이 깨졌을 때의 비상문이다.
export const LOD_STRICT_BAKED = (() => {
  try { return new URLSearchParams(location.search).get('lodstrict') !== '0'; }
  catch { return true; }   // 워커·SSR 등 location 이 없는 자리 — 엄격이 기본이다
})();

export const LOD_TOP_QUANT = (() => {
  try {
    const v = Number(new URLSearchParams(location.search).get('lodyq'));
    return Number.isFinite(v) && v >= 1 ? Math.floor(v) : 1;
  } catch { return 1; }
})();

export const LOD_TOP_CUBE = (() => {
  try { return new URLSearchParams(location.search).get('lodcube') === '1'; }
  catch { return false; }   // 워커·SSR 등 location 이 없는 자리 — 판때기가 기본값이다
})();
